import { useEffect, useState } from 'react'
import { CurieSelect } from '~/components/common/CurieSelect'
import { useHl7Codes } from '~/hooks/useHl7Codes'
import { HL7SchemaType } from '~/protos/agents'
import { SchemaTypeProps } from './types'

export interface Hl7SchemaData {
  hl7Version: string
  hl7Message: string
  hl7SegmentTerminator: string
  hl7SchemaType: string
}

export function Hl7({ sourceDataType, samples, onChange, onSchemaChange }: SchemaTypeProps) {
  const { data: hl7Codes } = useHl7Codes()
  const [hl7Version, setHl7Version] = useState<string>('v2_1')
  const [hl7Message, setHl7Message] = useState<string>('')
  const [hl7SegmentTerminator, setHl7SegmentTerminator] = useState<string>('CR')
  const [hl7SchemaType, setHl7SchemaType] = useState<string>('hl7')

  useEffect(() => {
    if (sourceDataType === 'DATA_TYPE_HL7') {
      onSchemaChange({
        hl7Version,
        hl7Message,
        hl7SegmentTerminator,
        hl7SchemaType
      })
    }
  }, [hl7Version, hl7Message, hl7SegmentTerminator, hl7SchemaType, sourceDataType, onSchemaChange])

  // Create arrays of HL7 version and message type options for select dropdowns
  const hl7VersionOptions = hl7Codes
    ? Object.keys(hl7Codes).map(version => ({
        label: version,
        key: version
      }))
    : []

  const hl7MessageOptions =
    hl7Codes && hl7Version
      ? (hl7Codes[hl7Version] || []).map(message => ({
          label: message.replace(/.esl$/, ''),
          key: message.replace(/.esl$/, '')
        }))
      : []

  const hl7SegmentTerminatorOptions = [
    { label: 'CR (default)', key: 'CR' },
    { label: 'LF', key: 'LF' }
  ]

  const hl7SchemaTypeOptions = [
    { label: 'hl7', key: 'hl7' },
    { label: 'hl7lax', key: 'hl7lax' }
  ]

  if (sourceDataType !== 'DATA_TYPE_HL7') {
    return null
  }

  return (
    <div className='hl7-selection-container mt-4'>
      <div className='grid grid-cols-2 gap-4 mb-4'>
        <div>
          <div className='label font-semibold text-xs text-default-700 mb-[10px] select-none'>
            Version
          </div>
          <CurieSelect
            items={hl7VersionOptions}
            placeholder='Select HL7 version'
            selectedKeys={[hl7Version]}
            onSelectionChange={selectedKeys => {
              if (typeof selectedKeys === 'string' || selectedKeys.size === 0) {
                return
              }
              const version = selectedKeys.values().next().value as string
              setHl7Version(version)
              // Reset message type when version changes
              if (hl7Codes && hl7Codes[version] && hl7Codes[version].length > 0) {
                setHl7Message(hl7Codes[version][0]?.replace(/.esl$/, ''))
              }
            }}
          />
        </div>
        <div>
          <div className='label font-semibold text-xs text-default-700 mb-[10px] select-none'>
            Message
          </div>
          <CurieSelect
            items={hl7MessageOptions}
            placeholder='Select HL7 message'
            selectedKeys={[hl7Message]}
            onSelectionChange={selectedKeys => {
              if (typeof selectedKeys === 'string' || selectedKeys.size === 0) {
                return
              }
              const message = selectedKeys.values().next().value as string
              setHl7Message(message)
            }}
          />
        </div>
      </div>
      <div className='grid grid-cols-2 gap-4'>
        <div>
          <div className='label font-semibold text-xs text-default-700 mb-[10px] select-none'>
            Segment Terminator
          </div>
          <CurieSelect
            items={hl7SegmentTerminatorOptions}
            placeholder='Select terminator'
            selectedKeys={[hl7SegmentTerminator]}
            onSelectionChange={selectedKeys => {
              if (typeof selectedKeys === 'string' || selectedKeys.size === 0) {
                return
              }
              const terminator = selectedKeys.values().next().value as string
              setHl7SegmentTerminator(terminator)
            }}
          />
        </div>
        <div>
          <div className='label font-semibold text-xs text-default-700 mb-[10px] select-none'>
            Schema Type
          </div>
          <CurieSelect
            items={hl7SchemaTypeOptions}
            placeholder='Select schema type'
            selectedKeys={[hl7SchemaType]}
            onSelectionChange={selectedKeys => {
              if (typeof selectedKeys === 'string' || selectedKeys.size === 0) {
                return
              }
              const schemaType = selectedKeys.values().next().value as string
              setHl7SchemaType(schemaType)
            }}
          />
        </div>
      </div>
    </div>
  )
}

export function createHl7Schema(schemaData: Hl7SchemaData) {
  return HL7SchemaType.fromJSON({
    version: schemaData.hl7Version,
    message: schemaData.hl7Message,
    segmentTerminator: schemaData.hl7SegmentTerminator,
    schemaType: schemaData.hl7SchemaType
  })
}
